import { ComponentProps, RefObject, useLayoutEffect, useState } from 'react';
import type { Popover } from '@mantine/core';

type PopoverProps = ComponentProps<typeof Popover>;
type Side = 'bottom-start' | 'top-start';

/** Высота списка с поиском и подвалом — как `max-height` у `.rf-picker-combobox`. */
const LIST_HEIGHT = 420;
const LIST_VIEWPORT_SHARE = 0.6;
/** Поиск, вкладки адресной книги, три строки и подвал: на стороне, где не видно и трёх строк, списку не место. */
const MIN_LIST_HEIGHT = 290;
const EDGE_GAP = 8;
/** `offset` у Popover пикеров. */
const OFFSET = 8;

const MIDDLEWARES: PopoverProps['middlewares'] = {
    flip: false,
    shift: true,
    size: {
        padding: EDGE_GAP,
        apply: ({ availableHeight, elements }) =>
            elements.floating.style.setProperty('--rf-picker-available-height', `${availableHeight}px`),
    },
};

interface IRoom {
    below: number;
    above: number;
}

const roomAround = (anchor: HTMLElement): IRoom => {
    const rect = anchor.getBoundingClientRect();
    return {
        below: window.innerHeight - rect.bottom - OFFSET - EDGE_GAP,
        above: rect.top - OFFSET - EDGE_GAP,
    };
};

/** При открытии: снизу, если там помещается весь список, иначе — где места больше. */
const openingSide = ({ below, above }: IRoom): Side =>
    below < Math.min(LIST_HEIGHT, window.innerHeight * LIST_VIEWPORT_SHARE) && above > below
        ? 'top-start'
        : 'bottom-start';

/** Поле сдвинулось при открытом списке: сторона меняется, только если на ней не видно трёх строк, а напротив места больше. */
const keptSide = (side: Side, { below, above }: IRoom): Side => {
    const [here, there] = side === 'top-start' ? [above, below] : [below, above];
    if (here >= MIN_LIST_HEIGHT || there <= here) return side;
    return side === 'top-start' ? 'bottom-start' : 'top-start';
};

/**
 * Сторона и высота выпадающего списка с поиском — пропсы Popover пикера.
 *
 * Сторону выбирает только хук: при открытии — по месту под полем для полного списка, а пока список открыт —
 * заново на прокрутке и изменении окна (поле уезжает к краю: прокрутка страницы или таблицы, докрутка строки к
 * редактору), переставляя список на другую сторону, когда на своей не видно и трёх строк. Доступную высоту список
 * получает переменной `--rf-picker-available-height` и ужимается: поиск и подвал видны всегда.
 *
 * Свои flip и запирание стороны у Mantine выключены. Mantine запирает сторону по первому расчёту позиции, а тот
 * идёт раньше решения хука (эффекты Popover выполняются до эффектов пикера) и приходит асинхронно — список
 * запирался снизу у самого края окна. А flip ужатого по месту списка переполнения не видит и не срабатывает.
 */
export function usePickerDropdownPlacement(anchorRef: RefObject<HTMLElement | null>, opened: boolean) {
    const [position, setPosition] = useState<Side>('bottom-start');

    useLayoutEffect(() => {
        const anchor = anchorRef.current;
        if (!opened || !anchor) return;
        setPosition(openingSide(roomAround(anchor)));
        let frame = 0;
        const follow = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setPosition((side) => keptSide(side, roomAround(anchor))));
        };
        // первый кадр — поле, сдвинутое раскладкой сразу после открытия (редактор ячейки, докрутка строки)
        follow();
        window.addEventListener('scroll', follow, { capture: true, passive: true });
        window.addEventListener('resize', follow);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', follow, { capture: true });
            window.removeEventListener('resize', follow);
        };
    }, [anchorRef, opened]);

    return { position, middlewares: MIDDLEWARES, preventPositionChangeWhenVisible: false };
}
